import React, { useState } from 'react';
import './BFMenu.css';

// ============ TYPES ============

export interface BFMenuItem {
    /** Unique identifier for the menu item */
    id: string;
    /** Text label for the menu item */
    label: string;
    /** Optional icon component to display before the label */
    icon?: React.ReactNode;
    /** Whether the item is currently active */
    active?: boolean;
    /** Click handler for the menu item */
    onClick?: () => void;
    /** Optional href for link items */
    href?: string;
}

export interface BFMenuProps {
    /** Array of menu items */
    items: BFMenuItem[];
    /** Orientation of the menu */
    orientation?: 'horizontal' | 'vertical';
    /** Additional class name */
    className?: string;
}

// ============ MENU ITEM COMPONENT ============

interface MenuItemProps {
    item: BFMenuItem;
    isActive: boolean;
    onItemClick: () => void;
}

function MenuItem({ item, isActive, onItemClick }: MenuItemProps) {
    const className = `bf-menu-item ${isActive ? 'bf-menu-item-active' : ''}`;

    const handleClick = () => {
        onItemClick();
        if (item.onClick) {
            item.onClick();
        }
        // For links, let the default behavior happen (navigation)
    };

    const content = (
        <>
            {item.icon && <span className="bf-menu-item-icon">{item.icon}</span>}
            <span className="bf-menu-item-label">{item.label}</span>
        </>
    );

    if (item.href) {
        return (
            <a
                href={item.href}
                className={className}
                onClick={handleClick}
                aria-current={isActive ? 'page' : undefined}
            >
                {content}
            </a>
        );
    }

    return (
        <button type="button" className={className} onClick={handleClick} aria-current={isActive ? 'page' : undefined}>
            {content}
        </button>
    );
}

// ============ MAIN COMPONENT ============

export function BFMenu({ items, orientation = 'horizontal', className = '' }: BFMenuProps) {
    // Initialize active item from items array
    const initialActiveId = items.find((item) => item.active)?.id || items[0]?.id;
    const [activeItemId, setActiveItemId] = useState<string>(initialActiveId);

    const handleItemClick = (itemId: string) => {
        setActiveItemId(itemId);
    };

    return (
        <nav className={`bf-menu bf-menu-${orientation} ${className}`} role="navigation">
            {items.map((item) => (
                <MenuItem
                    key={item.id}
                    item={item}
                    isActive={activeItemId === item.id}
                    onItemClick={() => handleItemClick(item.id)}
                />
            ))}
        </nav>
    );
}
